import React from 'react';

import { EntityType } from '@/modules/common/types/entity-type';
import { IntegrationServices } from '@/modules/common/types/integrations';
import { ResolveOnboardingClientsConflictsModal } from '@/modules/onboarding/clients-setup/components/resolve-conflicts-modal/resolve-onboarding-clients-conflicts-modal';
import { useConnectedEntityIntegrations } from '@/modules/onboarding/common/hooks/use-connected-entity-integrations';
import { EntityReconciliationConfig } from '@/modules/onboarding/common/types/entity-reconciliation-config';
import { invalidateOnboardingClientRelatedQueries } from '@/modules/onboarding/common/utils/onboarding-query-invalidation';

import { ClientsTableFormValues, clientsTableSchema } from '../api/client-schema';
import {
  deleteClients,
  getClients,
  getClientsValidationReport,
  submitClients,
  updateClientsBulk,
} from '../api/clients';
import { AddOnboardingClientsModal } from '../components/add-clients-modal';
import { ImportOnboardingClientsModal } from '../components/import-clients-modal/import-onboarding-clients-modal';
import { getClientColumns } from '../constants/columns';
import { ClientFieldMapper, ClientFieldValues } from '../mappers/client-field-mapper';
import { Client, ClientDataReconciliationQueryKeys } from '../types/clients.types';

const CLIENTS_I18N_NAMESPACE = 'authenticated.onboarding.clients';

export function useClientsReconciliationConfig(): EntityReconciliationConfig<
  Client,
  ClientsTableFormValues,
  ClientFieldValues
> {
  const { integrations: connectedIntegrations } = useConnectedEntityIntegrations(EntityType.Client);

  return {
    entityKey: 'clients',
    tableId: 'onboarding-clients',
    integrationColumnsCount: connectedIntegrations.length,

    i18nNamespaces: [CLIENTS_I18N_NAMESPACE, 'common', 'authenticated.onboarding.common'],
    i18nKeys: {
      searchPlaceholder: `${CLIENTS_I18N_NAMESPACE}:filters.searchPlaceholder`,
      searchResultAll: `${CLIENTS_I18N_NAMESPACE}:searchResult.all`,
      searchResultCount: `${CLIENTS_I18N_NAMESPACE}:searchResult.count`,
      searchResultSelected: `${CLIENTS_I18N_NAMESPACE}:searchResult.selected`,
      deleteButton: `${CLIENTS_I18N_NAMESPACE}:delete`,
      clearSelection: `${CLIENTS_I18N_NAMESPACE}:clearSelection`,
      warningsModalTitle: `${CLIENTS_I18N_NAMESPACE}:warningsModal.title`,
      warningsModalText: `${CLIENTS_I18N_NAMESPACE}:warningsModal.text`,
      warningsModalConfirm: `${CLIENTS_I18N_NAMESPACE}:warningsModal.confirm`,
      warningsModalCancel: `${CLIENTS_I18N_NAMESPACE}:warningsModal.cancel`,
      noDataYet: `${CLIENTS_I18N_NAMESPACE}:noClientsYet`,
      noDataDescription: `${CLIENTS_I18N_NAMESPACE}:noClientsDescription`,
      submitEntitiesWithErrorsExist: `${CLIENTS_I18N_NAMESPACE}:submit.entitiesWithErrorsExist`,
      submitDuplicateEntitiesExists: `${CLIENTS_I18N_NAMESPACE}:submit.duplicateEntitiesExists`,
      deleteErrorFallback: `${CLIENTS_I18N_NAMESPACE}:api.error.deleteFailed`,
      updateErrorFallback: `${CLIENTS_I18N_NAMESPACE}:api.error.updateFailed`,
      deleteModalTitle: `${CLIENTS_I18N_NAMESPACE}:deleteModal.title`,
      deleteModalText: `${CLIENTS_I18N_NAMESPACE}:deleteModal.text`,
      deleteModalCancel: `${CLIENTS_I18N_NAMESPACE}:deleteModal.cancel`,
      deleteModalConfirm: `${CLIENTS_I18N_NAMESPACE}:deleteModal.confirm`,
      deleteModalAndMore: `${CLIENTS_I18N_NAMESPACE}:deleteModal.andMore`,
    },

    queryKeys: {
      entities: ClientDataReconciliationQueryKeys.Clients,
      validationReport: ClientDataReconciliationQueryKeys.ValidationReport,
    },
    getEntities: getClients,
    getValidationReport: getClientsValidationReport,

    getSchema: schemaT => clientsTableSchema(schemaT),
    defaultFormValues: { clients: [] } as unknown as ClientsTableFormValues,
    formFieldPrefix: 'clients',
    getColumns: ({ t: columnT, form, getFormIndex, extraDeps }) =>
      getClientColumns({
        t: columnT,
        form,
        getFormIndex,
        connectedIntegrations: extraDeps as IntegrationServices[],
      }),

    duplicateFieldExtractors: [(item: { name: string }) => item.name],

    fieldMapper: new ClientFieldMapper(),
    buildUpdateRequestItem: (entityId, changes) => ({
      id: entityId,
      name: changes.name,
    }),

    deleteEntities: ids => deleteClients({ clientIds: ids }),
    updateEntitiesBulk: updateClientsBulk,
    submitEntities: submitClients,
    invalidateQueries: invalidateOnboardingClientRelatedQueries,

    renderAddButton: onSuccess => React.createElement(AddOnboardingClientsModal, { onSuccess }),
    renderImportButton: onSuccess =>
      React.createElement(ImportOnboardingClientsModal, { onSuccess }),
    renderResolveDuplicatesModal: (selectedItems, onSuccess, options) =>
      React.createElement(ResolveOnboardingClientsConflictsModal, {
        selectedItems,
        onSuccess,
        connectedIntegrations,
        disabled: options?.disabled,
        disabledTooltip: options?.disabledTooltip,
      }),

    useExtraColumnDeps: () => connectedIntegrations,
  };
}
